// @owners { team: patients-team }
import { Toast, ToastContext } from '@alto/design-system';
import { useMutation } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { DEFAULT_ERROR_ALERT_MESSAGE } from '~shared/constants';
import { put } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { getProgynyWizardCycle, getProgynyWizardUpdateAttributes } from '~shared/selectors/checkout/progyny';
import { useSelectorShared } from '~shared/store';
import { type APIError, type FertilityCycle } from '~shared/types';

const API_VERSION_1 = 'v1';

type FertilityCycleResponse = { data: FertilityCycle } | { error: APIError };

export const useUpdateFertilityCycle = () => {
  const { addToast } = useContext(ToastContext);
  const fertilityCycleID = useSelectorShared(getProgynyWizardCycle)?.id;
  const fertility_cycle_attributes = useSelectorShared(getProgynyWizardUpdateAttributes);
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      if (!fertilityCycleID) {
        addToast(<Toast variant="error">Missing fertility cycle ID. {DEFAULT_ERROR_ALERT_MESSAGE}</Toast>);
        return false;
      }

      const response: FertilityCycleResponse = await put(
        `/fertility_cycles/${fertilityCycleID}`,
        { fertility_cycle_attributes },
        { version: API_VERSION_1 },
      );
      if ('error' in response) {
        addToast(<Toast variant="error">{getErrorMessageFromResponse(response)}</Toast>);
        return false;
      }
      return response.data;
    },
  });

  return { updateFertilityCycle: mutateAsync, isPending };
};
